import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';

// Page transition wrapper
const pageVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 },
};

// Animated section component
function AnimatedSection({ children, className, delay = 0 }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.div>
  );
}

function DigitalInteractive() {
  const [gif1Hovered, setGif1Hovered] = useState(false);
  const [gif2Hovered, setGif2Hovered] = useState(false);
  const [gif3Hovered, setGif3Hovered] = useState(false);

  const labelPartners = [
    { name: 'Universal Music Group', type: 'Major Label' },
    { name: 'Sony Music Entertainment', type: 'Major Label' },
    { name: 'Warner Music Group', type: 'Major Label' },
  ];

  const feelsFeatures = [
    {
      title: 'Share Anywhere',
      description: 'Send Feels messages directly to texts, social media, DMs, or group chats.',
      icon: '↗',
    },
    {
      title: 'Discover Trending',
      description: 'Explore trending Feels, curated collections, or search by artist, emotion, song title, and lyrics.',
      icon: '◎',
    },
    {
      title: 'Create Custom Feels',
      description: 'Highlight lyrics, customize fonts and backgrounds, and upload to the community library.',
      icon: '✦',
    },
    {
      title: 'Artist Engagement',
      description: 'Artists share Feels, video sneak peeks, merch links, concert info, and more.',
      icon: '♫',
    },
    {
      title: 'Keyboard Integration',
      description: 'Seamlessly share Feels via an intuitive keyboard extension, like an emoji keyboard.',
      icon: '⌨',
    },
  ];

  return (
    <motion.div
      className="digital-page"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.5 }}
    >
      {/* Page Hero - Playful */}
      <section className="page-hero playful">
        <div className="container">
          <motion.div
            className="page-hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <span className="page-label">Portfolio</span>
            <h1 className="page-title playful-title">
              <span className="title-line">Digital &</span>
              <span className="title-line title-italic">Interactive</span>
            </h1>
            <p className="page-subtitle">
              Creating digital experiences that connect people through <em>music, art, and code.</em>
            </p>
          </motion.div>
        </div>
        <div className="hero-decoration" />
      </section>

      {/* Feels Music App - Featured */}
      <section className="project-feature feels-section">
        <div className="container">
          <AnimatedSection className="project-feature-header playful">
            <motion.span
              className="floating-tag tag-cyan"
              initial={{ rotate: 2, opacity: 0 }}
              whileInView={{ rotate: 2, opacity: 1 }}
              viewport={{ once: true }}
            >
              CPO (Chief Product Officer)
            </motion.span>
            <h2>
              Feels<br/>
              <span className="title-italic">Music App</span>
            </h2>
            <p className="project-tagline">
              Redefining How People <em>Connect</em> Through Music
            </p>
          </AnimatedSection>

          {/* App Preview */}
          <AnimatedSection className="feels-preview-split" delay={0.1}>
            <div className="feels-split-left">
              <p className="feels-intro">
                Feels is a next-generation music messaging app that redefines how people
                connect through sound. Designed for a generation that expresses itself
                beyond text, Feels lets users send and receive short-form music video
                snippets that capture emotions, moods, and moments in a way words alone cannot.
              </p>
              <a
                href="https://lnk.to/feelsmusic"
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-primary"
              >
                Download Feels
              </a>
            </div>
            <div className="feels-split-right">
              <img
                src="/images/feels/hero-img.png"
                alt="Feels App Interface"
              />
            </div>
          </AnimatedSection>

          {/* Industry Partnerships */}
          <AnimatedSection className="partnerships-section" delay={0.2}>
            <h3>Music Licensing</h3>
            <p className="partnerships-intro">
              Acquired music licenses with major and indie labels, bringing
              millions of songs to the platform.
            </p>
            <div className="partners-grid">
              {labelPartners.map((partner, index) => (
                <motion.div
                  key={partner.name}
                  className="partner-card"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -5 }}
                >
                  <span className="partner-type">{partner.type}</span>
                  <h4>{partner.name}</h4>
                </motion.div>
              ))}
            </div>
          </AnimatedSection>

          {/* Features */}
          <AnimatedSection className="features-section" delay={0.3}>
            <h3>Creating Digital Music Experiences</h3>
            <div className="features-grid">
              {feelsFeatures.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  className="feature-card"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -5, borderColor: 'rgba(6, 182, 212, 0.5)' }}
                >
                  <span className="feature-icon">{feature.icon}</span>
                  <h4>{feature.title}</h4>
                  <p>{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </AnimatedSection>

          {/* My Role */}
          <AnimatedSection className="role-section" delay={0.4}>
            <div className="role-content">
              <h3>My Role: Bridging Music & Technology</h3>
              <div className="role-grid">
                <div className="role-card">
                  <h4>Industry Relationships</h4>
                  <p>
                    Building trust with labels, managers, and artists. Walking stakeholders
                    through demos and translating their needs into product features.
                  </p>
                </div>
                <div className="role-card">
                  <h4>Experience Design</h4>
                  <p>
                    Designing micro-moments of musical discovery. Creating flows that
                    encourage emotional expression through sound, not just sharing songs.
                  </p>
                </div>
                <div className="role-card">
                  <h4>Product Strategy</h4>
                  <p>
                    Leading product vision for a platform that's the future of music
                    communication - one song at a time.
                  </p>
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Creative Coding */}
      <section className="project-section alt-bg">
        <div className="container">
          <AnimatedSection className="project-header playful">
            <span className="section-tag">Creative Technology</span>
            <h2>
              Creative Coding<br/>
              <span className="title-italic">/ p5.js</span>
            </h2>
            <p className="project-tagline">
              Interactive Digital Experiences & <em>Generative Art</em>
            </p>
          </AnimatedSection>

          <AnimatedSection className="creative-coding-content" delay={0.1}>
            <p className="lead-text">
              Exploring the intersection of code and creativity through interactive
              installations, generative art, and coded experiences that respond to
              human input.
            </p>

            <div className="coding-projects">
              <motion.div
                className="coding-project"
                whileHover={{ scale: 1.02 }}
                transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                onMouseEnter={() => setGif1Hovered(true)}
                onMouseLeave={() => setGif1Hovered(false)}
              >
                <div className="project-preview gif-hover-play">
                  <img
                    src={gif1Hovered
                      ? "/images/digital/C_C_1-optimized.gif"
                      : "/images/digital/C_C_1-still.png"
                    }
                    alt="Interactive data visualization"
                  />
                </div>
                <div className="project-info">
                  <h4>Data Visualization</h4>
                  <p>Interactive visualization responding to user input</p>
                </div>
              </motion.div>

              <motion.div
                className="coding-project"
                whileHover={{ scale: 1.02 }}
                transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                onMouseEnter={() => setGif2Hovered(true)}
                onMouseLeave={() => setGif2Hovered(false)}
              >
                <div className="project-preview gif-hover-play">
                  <img
                    src={gif2Hovered
                      ? "/images/digital/Creative-Coding-optimized-2.gif"
                      : "/images/digital/Creative-Coding-still-2.png"
                    }
                    alt="Interactive experience"
                  />
                </div>
                <div className="project-info">
                  <h4>Motion Response</h4>
                  <p>Code-driven experience responding to cursor movement</p>
                </div>
              </motion.div>

              <motion.div
                className="coding-project"
                whileHover={{ scale: 1.02 }}
                transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                onMouseEnter={() => setGif3Hovered(true)}
                onMouseLeave={() => setGif3Hovered(false)}
              >
                <div className="project-preview gif-hover-play">
                  <img
                    src={gif3Hovered
                      ? "/images/digital/Creative-Coding-optimized-3.gif"
                      : "/images/digital/Creative-Coding-still-3.png"
                    }
                    alt="Interactive pattern"
                  />
                </div>
                <div className="project-info">
                  <h4>Input Reactive</h4>
                  <p>Dynamic patterns generated from real-time input</p>
                </div>
              </motion.div>
            </div>

            <div className="skills-tags">
              <span className="skill-tag">p5.js</span>
              <span className="skill-tag">JavaScript</span>
              <span className="skill-tag">Processing</span>
              <span className="skill-tag">WebGL</span>
              <span className="skill-tag">Interactive Design</span>
              <span className="skill-tag">Generative Art</span>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Motion Graphics */}
      <section className="project-section">
        <div className="container">
          <AnimatedSection className="project-header playful">
            <span className="section-tag tag-pink">Motion Design</span>
            <h2>
              Motion<br/>
              <span className="title-italic">Graphics</span>
            </h2>
            <p className="project-tagline">
              Bringing Stories to Life Through <em>Movement</em>
            </p>
          </AnimatedSection>

          <AnimatedSection className="motion-content" delay={0.1}>
            {/* Kinetic Typography Feature */}
            <div className="kinetic-feature">
              <h3>Kinetic Typography</h3>
              <p className="lead-text">
                Animated lyrics synchronized with "Deja Vu" by Olivia Rodrigo,
                bringing the song's emotional journey to life through movement and typography.
              </p>

              <div className="kinetic-video-container">
                <video
                  controls
                  poster="/images/digital/DejaVu-Still1.png"
                  className="kinetic-video"
                >
                  <source src="/images/digital/deja-vu-kinetic-typography.mp4" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>

              <div className="kinetic-stills">
                <img src="/images/digital/DejaVu-Still1.png" alt="Deja Vu typography frame" />
                <img src="/images/digital/DejaVu-Still2.png" alt="Deja Vu typography frame" />
                <img src="/images/digital/DejaVu-Still3.png" alt="Deja Vu typography frame" />
                <img src="/images/digital/DejaVu-Still4.png" alt="Deja Vu typography frame" />
                <img src="/images/digital/DejaVu-Still5.png" alt="Deja Vu typography frame" />
                <img src="/images/digital/DejaVu-Still6.png" alt="Deja Vu typography frame" />
              </div>

              <div className="skills-tags" style={{ marginTop: 'var(--space-6)' }}>
                <span className="skill-tag">After Effects</span>
                <span className="skill-tag">Illustrator</span>
              </div>
            </div>

            {/* Looping Animation */}
            <div className="looping-animation-feature">
              <h3>Looping Animation</h3>
              <p className="lead-text">
                A hypnotic looping animation exploring color theory and rhythm,
                designed using only three colors at full saturation and brightness.
              </p>

              <div className="looping-animation-preview">
                <img
                  src="/images/digital/Animation-Yellow.Gif"
                  alt="Looping animation with three saturated colors"
                />
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Artesia */}
      <section className="project-section alt-bg">
        <div className="container">
          <AnimatedSection className="project-header playful">
            <span className="section-tag">Web Development & Product</span>
            <h2>
              Artesia<br/>
              <span className="title-italic">AI Ideation Platform</span>
            </h2>
            <p className="project-tagline">
              Unleashing Creativity Through <em>Innovation</em>
            </p>
          </AnimatedSection>

          <AnimatedSection className="artesia-content" delay={0.1}>
            <div className="artesia-intro">
              <img
                src="/images/digital/Artesia-1.png"
                alt="Artesia"
                className="artesia-header-img"
              />
              <p className="lead-text">
                Artesia is a web-based platform that accelerates the creative process using
                AI-powered object detection and a virtual whiteboard interface. By harnessing
                the power of AI, designers can explore diverse reference materials effortlessly,
                fostering creativity and innovation.
              </p>
            </div>

            <div className="artesia-demo">
              <img
                src="/images/digital/prod.gif"
                alt="Artesia platform demo"
                className="artesia-demo-gif"
              />
            </div>

            <div className="artesia-details">
              <div className="artesia-problem">
                <h4>The Problem</h4>
                <p>
                  Designers face constraints in their creative journey due to limited ideation tools.
                  This impedes their ability to form unexpected connections and generate truly unique ideas efficiently.
                </p>
              </div>
              <div className="artesia-solution">
                <h4>The Solution</h4>
                <p>
                  AI-powered object detection analyzes reference materials and suggests unexpected connections,
                  helping creatives break through mental blocks and discover novel ideas.
                </p>
              </div>
            </div>

            <div className="skills-tags">
              <span className="skill-tag">Web Development</span>
              <span className="skill-tag">Product Management</span>
              <span className="skill-tag">User Research</span>
              <span className="skill-tag">Product Design</span>
              <span className="skill-tag">AI Integration</span>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* CTA - Playful */}
      <section className="page-cta playful">
        <div className="container">
          <AnimatedSection className="cta-content-playful">
            <h2>
              See the Full <em>Picture</em>
            </h2>
            <p>
              Explore my immersive physical installations and concept designs.
            </p>
            <div className="cta-links">
              <Link to="/experiential/immersive" className="btn btn-primary">
                Immersive Experiences
              </Link>
              <Link to="/experiential/concepts" className="btn btn-primary">
                Concepts
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </motion.div>
  );
}

export default DigitalInteractive;
